/**
 * Security Utilities
 * 
 * Provides HTTPS enforcement and other security-related functionality.
 */

/**
 * Check if the current connection is secure (HTTPS)
 */
export function isSecureConnection(): boolean {
  return window.location.protocol === 'https:' || 
         window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1';
}

/**
 * Redirect to HTTPS if not on a secure connection
 * Call this early in app initialization
 * 
 * NOTE: Real HTTPS enforcement belongs at the CDN / edge (e.g., Cloudflare)
 * level. This client-side check is only a fallback and can be bypassed.
 */
export function enforceHttps(): void {
  // Skip for local development
  if (window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1') {
    return;
  }
  
  // Redirect HTTP to HTTPS
  if (window.location.protocol === 'http:') {
    const httpsUrl = window.location.href.replace('http:', 'https:');
    window.location.replace(httpsUrl);
  }
}

/**
 * Security headers check for development
 * Logs warnings if security best practices aren't followed
 */
export function checkSecurityHeaders(): void {
  if (import.meta.env.DEV) {
    // In development, check for common security issues
    const issues: string[] = [];
    
    // Check for HTTPS in production warning
    if (window.location.protocol === 'https:') {
      console.log('✓ HTTPS is enabled');
    } else if (window.location.hostname !== 'localhost') {
      issues.push('⚠ Not using HTTPS in production');
    }
    
    if (issues.length > 0) {
      console.warn('Security Check:', issues.join('\n'));
    }
  }
}

/**
 * Prevent clickjacking by checking if app is in iframe
 */
export function preventClickjacking(): void {
  if (window.top && window.top !== window.self) {
    // App is running in an iframe - potential clickjacking risk
    console.warn('Application is running in an iframe - potential clickjacking risk');
    
    // Break out of iframe or clear the page
    try {
      window.top.location.href = window.self.location.href;
    } catch {
      // If we can't navigate the parent, clear the body to prevent UI interaction
      document.body.innerHTML = '';
      document.body.style.display = 'none';
    }
  }
}

/**
 * Sanitize filename for safe download
 */
export function sanitizeFilename(filename: string): string {
  // Remove path traversal attempts
  const cleanName = filename.replace(/^(.*[/\\])?/, '');
  
  // Replace dangerous characters
  return cleanName
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\.+/g, '.')
    .trim() || 'download';
}
